import * as React from 'react';
import { PauseView, PauseViewProps } from './Pause.view';
import {
  CategoryArchiveFilter,
  GetCategoriesLazyQueryHookResult,
  GetCategoriesQuery,
  GetQueueMetadataLazyQueryHookResult,
  GetSocialProfilesLazyQueryHookResult,
  QueueMetaInput,
  UpdatePauseSettingsMutationFn,
  UpdatePauseSettingsPerProfileCategoryMutationFn,
  UpdateWorkspaceSettingsInput
} from '../../../models/graphql';
import { useSettings } from '../../../hooks/useSettings';
import { AppContext } from 'providers/AppProvider';
import { useDidMount } from 'hooks/useDidMount';
import { triggerErrorToast } from 'helpers/triggerErrorToast';
import { GetSocialProfiles_workspace_socialProfiles } from '../../../models/legacy-graphql';

export type SwitchButtonType = {
  title: 'RSS Auto-Importers' | 'Post Publishing';
  item: 'autoImportsPaused' | 'queuesPaused';
  description: string | JSX.Element;
};
export enum PauseStates {
  'Active' = 'Active',
  'Paused' = 'Paused'
}
export type OnUpdateWorkspacePauseSettings = (data: {
  item: 'autoImportsPaused' | 'queuesPaused';
  active: boolean;
}) => () => void;
export type OnUpdatePausePerProfileCategory = (
  data: QueueMetaInput[],
  complete?: (keepValue?: boolean) => void
) => void;
export type PauseSettingsControllerProps = {
  updateWorkspacePauseSettings: UpdatePauseSettingsMutationFn;
  updatePauseSettingsPerProfileCategory: UpdatePauseSettingsPerProfileCategoryMutationFn;
  getCategoriesQuery: GetCategoriesLazyQueryHookResult[0];
  getSocialProfilesQuery: GetSocialProfilesLazyQueryHookResult[0];
  getQueueMetadataQuery: GetQueueMetadataLazyQueryHookResult[0];
  categoriesResults: GetCategoriesLazyQueryHookResult[1];
  socialProfilesResults: GetSocialProfilesLazyQueryHookResult[1];
  queueMetadataResults: GetQueueMetadataLazyQueryHookResult[1];
};
export const PauseController = (props: PauseSettingsControllerProps): JSX.Element => {
  const [switchButtonSubmitted, setSwitchButtonSubmitted] = React.useState(false);
  const [customPublishingSubmitted, setCustomPublishingSubmitted] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [loadingError, setLoadingError] = React.useState(false);
  const [profileModal, setProfileModal] = React.useState<GetSocialProfiles_workspace_socialProfiles | null>(null);
  const [categoryModal, setCategoryModal] = React.useState<
    GetCategoriesQuery['workspace']['categories'][number] | null
  >(null);
  const { selectedWorkspaceSettings } = useSettings();
  const { selectedWorkspaceId } = React.useContext(AppContext);
  const { socialProfiles, categories, queueMeta } = React.useMemo(
    () => ({
      socialProfiles: props.socialProfilesResults.data?.workspace.socialProfiles || [],
      categories: props.categoriesResults.data?.workspace.categories || [],
      queueMeta: props.queueMetadataResults.data?.workspace.queueMeta || []
    }),
    [props.socialProfilesResults, props.categoriesResults, props.queueMetadataResults]
  );
  const fetchData = async () => {
    try {
      setLoading(true);
      if (selectedWorkspaceId) {
        await Promise.all([
          props.getCategoriesQuery({
            variables: { workspaceId: selectedWorkspaceId, statusFilter: CategoryArchiveFilter.ACTIVE }
          }),
          props.getSocialProfilesQuery({ variables: { workspaceId: selectedWorkspaceId } }),
          props.getQueueMetadataQuery({ fetchPolicy: 'network-only', variables: { workspaceId: selectedWorkspaceId } })
        ]);
      }
      setLoadingError(false);
    } catch (e) {
      setLoadingError(true);
      triggerErrorToast('Pause Settings Error')(e);
    } finally {
      setLoading(false);
    }
  };
  const onUpdateWorkspacePauseSettings: OnUpdateWorkspacePauseSettings =
    ({ active, item }) =>
    async () => {
      if (!selectedWorkspaceId || !selectedWorkspaceSettings || switchButtonSubmitted) return;
      try {
        setSwitchButtonSubmitted(true);
        let value = null;
        if (active && selectedWorkspaceSettings?.[item]) {
          value = false;
        } else if (!active && !selectedWorkspaceSettings?.[item]) {
          value = true;
        }

        if (value !== null) {
          const updateWorkspaceSettings = { ...selectedWorkspaceSettings };
          updateWorkspaceSettings[item] = value;
          const input: UpdateWorkspaceSettingsInput = {};
          switch (item) {
            case 'autoImportsPaused':
              input.pauseAutoImports = value;
              break;
            case 'queuesPaused':
              input.pauseQueues = value;
              break;
          }
          await props.updateWorkspacePauseSettings({
            variables: {
              input,
              workspaceId: selectedWorkspaceId
            },
            optimisticResponse: {
              updateWorkspaceSettings
            }
          });
        }
      } catch (e) {
        triggerErrorToast('Pause Settings Error')(e);
      } finally {
        setSwitchButtonSubmitted(false);
      }
    };
  const onUpdatePerProfileCategory: OnUpdatePausePerProfileCategory = async (input, complete) => {
    if (customPublishingSubmitted) return;
    try {
      setCustomPublishingSubmitted(true);
      if (selectedWorkspaceId) {
        // const optimisticResponse = {
        //   bulkUpdateQueueMeta: input.map(item => {
        //     const category = categories.find(category => category.id === item.categoryId);
        //     const profile = socialProfiles.find(profile => profile.id === item.socialProfileId);
        //
        //     return {
        //       category: {
        //         id: category?.id,
        //         __typename: category?.__typename
        //       },
        //       paused: item.paused,
        //       socialProfile: {
        //         id: profile?.id,
        //         __typename: profile?.__typename
        //       },
        //       __typename: 'QueueMetadata'
        //     };
        //   }) as UpdatePauseSettingsPerProfileCategory_bulkUpdateQueueMeta[]
        // };
        // TODO It seems that fetch-policy of network-only & optimistic response is broken - near line 78
        // TODO @see props.getQueueMetadataQuery({ fetchPolicy: 'network-only'
        // TODO @see https://github.com/apollographql/apollo-client/issues/10802

        await props.updatePauseSettingsPerProfileCategory({
          variables: { workspaceId: selectedWorkspaceId, input }
          // optimisticResponse
        });
      }
      setProfileModal(null);
      setCategoryModal(null);
    } catch (e) {
      triggerErrorToast('Pause Settings Error')(e);
    } finally {
      setCustomPublishingSubmitted(false);
      if (complete) {
        complete();
      }
    }
  };
  const switches: SwitchButtonType[] = React.useMemo(
    () => [
      {
        title: 'RSS Auto-Importers',
        description: "While 'Paused', no new Posts will be imported from your RSS Auto-Importers.",
        item: 'autoImportsPaused'
      },
      {
        title: 'Post Publishing',
        description: (
          <>
            While &apos;Paused&apos;, <strong>none</strong> of your queued or scheduled Posts will be published. This
            may be useful while you&apos;re getting started, or if an event happens where it would be insensitive to
            carry on posting as normal. You can keep this &apos;Active&apos;, and pause specific Categories or Profiles
            below.
          </>
        ),
        item: 'queuesPaused'
      }
    ],
    []
  );

  useDidMount(() => {
    fetchData().finally();
  });

  const viewProps: PauseViewProps = {
    selectedWorkspaceSettings,
    switches,
    onUpdateWorkspacePauseSettings,
    socialProfiles,
    categories,
    onUpdatePerProfileCategory,
    queueMeta,
    profileModal,
    setProfileModal,
    categoryModal,
    setCategoryModal,
    switchButtonSubmitted,
    customPublishingSubmitted,
    loading,
    loadingError
  };
  return <PauseView {...viewProps} />;
};
